import { ViewStateType } from 'components/ProductTemplates/types';
import { MessageType } from 'views/shared/FeedbackTemplate';
import { ActionMap } from './commonTypes';

export enum Types {
  Delete = 'DELETE_PRODUCT',
  Add = 'ADD_PRODUCT',
}

export type ProductType = {
  id: number;
  name: string;
  price: number;
};

type ProductPayload = {
  [Types.Add]: {
    id: number;
    name: string;
    price: number;
  };
  [Types.Delete]: {
    id: number;
  };
};

export type ProductActions = ActionMap<ProductPayload>[keyof ActionMap<ProductPayload>];

export interface Region {
  id: string;
  text: string;
  color: string;
  value: string;
  textColor?: string;
}

interface HoldingStockItem {
  rotationNumber: string;
  dealDate: string;
  location: string;
}

export interface HistoricMarketPriceItem {
  date: string;
  marketPrice: number;
}

export interface BaseProduct {
  id: string;
  holdingId?: string;
  assetId: number;
  assetUnitId?: string;
  lwin18: string;
  wineName: string;
  vintage: string;
  region: string;
  dealDate: string;
  dealRef: string;
  dealCCY: string;
  unit: string;
  unitCount: number;
  qty: number;
  location?: string;
  status?: string;
  portfolioId?: number;
  rotationNumber?: string;
  sanitized_wine_name?: string;
}

interface CurrentHolding extends BaseProduct {
  avgProfitAndLoss: number;
  qtyForSale: number;
  cultWinesAllocationRegion?: string;
  qtySold?: number;
  soldDate?: string;
  soldPricePerUnit?: number;
  cashOffer?: number;
  costPerUnit: number;
  avgCost?: number;
  totalCost: number;
  valuePerUnit: number;
  totalValue: number;
  changedPct: number;
  totalMgmtFee: number;
  totalCostWithMgmtFee: number;
  netPosition: number;
  profitAndLoss: number;
  profitAndLossPerUnit?: number;
  netPositionPerUnit?: number;
  costWithMgmtFeePerUnit?: number;
  mgmtFeePerUnit?: number;
  profitAndLossHistory: {
    date: string;
    profitAndLoss: number;
  }[];
  holdingStocks?: HoldingStockItem[];
  historicMarketPrices?: HistoricMarketPriceItem[];
  priceForSale: number;
  highestBid?: ActiveMarketOrder | null;
  lowestOffer?: ActiveMarketOrder | null;
  highestBidPrice?: number | null;
  lowestOfferPrice?: number | null;
  latestDealDate: Date | null;
  physicalQuantity: number;
}

interface ActiveMarketOrder {
  price: number | null;
  priceGbp: number | null;
  priceInTargetCurrency: number | null;
  orderType: OrderType | null;
  orderCreatedDatetime: string | null;
  quantity: number | null;
  quantityOutstanding: number | null;
}
enum OrderType {
  Regular = 'Regular',
  Campaign = 'Campaign',
  CX = 'CX',
  Cwi = 'Cwi',
  CwBid = 'CwBid',
  CwBuy = 'CwBuy',
}

export interface AcceptBidEventType {
  viewStateType: ViewStateType;
  messageType?: MessageType;
}
/** Vinous review data */
interface VinousReview {
  reviewer?: string | null;
  reviewDate?: string | null;
  score?: number | null;
  drinkFrom?: number | null;
  drinkTo?: number | null;
  tastingNote?: string | null;
  externalLink?: string | null;
}

/** Wine Advocate review data */
interface WineAdvocateReview {
  reviewer?: string | null;
  scoreDate?: string | null;
  score?: number | null;
  drinkYearMin?: number | null;
  drinkYearMax?: number | null;
  tastingNote?: string | null;
  articleUrl?: string | null;
}

export interface Product extends CurrentHolding {
  imageFileName?: string;
  imageUrl?: string;
  color?: string;
  description?: string;
  stockId?: string;
  /** AI-generated tasting notes for the wine */
  tastingNotes?: string;
  /** Combined critic score (e.g., Wine Advocate, Wine Spectator average) */
  combinedScore?: number;
  /** Wine Advocate rating score */
  wa?: number | null;
  /** Vinous rating score as string */
  vinous?: string | null;
  /** Vinous review details */
  vinousReview?: VinousReview;
  /** Wine Advocate review details */
  wineAdvocateReview?: WineAdvocateReview;
  /** Quality metrics - Grade score out of 10 */
  grade?: number | null;
  /** Quality metrics - Fair value score out of 100 */
  fairValueScore?: number | null;
  /** Quality metrics - Liquidity score out of 100 */
  liquidityScore?: number | null;
  /** Quality metrics - Performance score out of 100 */
  performanceScore?: number | null;
  /** Quality metrics - Vintage score out of 100 */
  vintageScore?: number | null;
  /** Market price from portal holdings */
  marketPrice?: number | null;
}

export enum DATA_REFS {
  PORTFOLIO_ID = 'portfolioId',
  ASSETID = 'lwin18',
  NAME = 'wineName',
  DEAL_DATE = 'dealDate',
  UNIT = 'unit',
  QUANTITY = 'qty',
  AVG_COST = 'avgCost',
  COST_PER_UNIT = 'costPerUnit',
  AVG_COST_PER_UNIT = 'avgCostPerUnit',
  AVG_VALUE_PER_UNIT = 'avgValuePerUnit',
  COST_PER_UNIT_INCLUDING_MANAGEMENT_FEE = 'costWithMgmtFeePerUnit',
  TOTAL_COST = 'totalCost',
  TOTAL_COST_INCLUDING_MANAGEMENT_FEE = 'totalCostWithMgmtFee',
  VALUE_PER_UNIT = 'valuePerUnit',
  TOTAL_VALUE = 'totalValue',
  PERCENT_CHANGE = 'changedPct',
  PROFIT_LOSS = 'profitAndLoss',
  PROFIT_LOSS_PER_CASE = 'profitAndLossPerCase',
  REGION = 'region',
  VINTAGE = 'vintage',
  SOLD_DATE = 'soldDate',
  QTY_SOLD = 'qtySold',
  SOLD_PER_UNIT = 'soldPricePerUnit',
  STATUS = 'status',
  LOCATION = 'location',
  CASH_OFFER = 'cashOffer',
  ROTATION_NUMBER = 'rotationNumber',
  SANITIZED_WINE_NAME = 'sanitized_wine_name',
  NET_POSITION = 'netPosition',
  CULT_WINES_ALLOCATION_REGION = 'cultWinesAllocationRegion',
  PRICE_FOR_SALE = 'priceForSale',
  HIGHEST_LIVE_BID = 'highestBidPrice',
  LOWEST_OFFER = 'lowestOfferPrice',
  LAST_PURCHASED_DATE = 'latestDealDate',
}

interface WineAsset {
  wineName: string;
  valuePerUnit: number;
  vintage: string;
  lwin18: string;
  unitsize: string;
  imageUrl: string;
  changePct: number;
  changePctFomated: string;
  color: string;
  pnl?: number;
  quantity?: number;
  criticScore?: number | null;
}
